/**
 * Timeseries prefetch + math.js fetcher registry for derive expressions.
 *
 * Background — math.js evaluation is synchronous per-row, but CDF datapoints
 * fetches are async and best done batched-globally. This module bridges the
 * gap with a two-phase design:
 *
 * 1. **AST analysis (sync).** Parse each derive expression's AST and walk
 *    for fetcher calls (`datapoints`, `hourly`, `daily`, `weekly`,
 *    `monthly`). Each call yields a static `(columnRef, fnName, opts)`
 *    tuple — the basis for spec deduplication.
 * 2. **Prefetch + registry (async).** For each unique `(fnName, opts)`
 *    spec, walk all rows to collect every externalId referenced, batch
 *    via {@link fetchDatapointsBatched} (cache-aware), and assemble a
 *    `(specHash, externalId) → Datapoints` lookup. The lookup is closed
 *    over by the runtime fetcher functions, which dispatch on their first
 *    argument: a ref-shaped value triggers a lookup; a pre-fetched array
 *    triggers client-side bucketing (for nested usage like
 *    `monthly(datapoints(ref))`).
 *
 * The runtime functions are injected into the per-row scope by
 * {@link evaluateDerivedFields}; the static helpers (`value`, `values`,
 * `timestamps`, `first`, `last`) are registered globally in
 * {@link math-config.ts}.
 */
import type { CogniteClient, DatapointAggregate } from '@cognite/sdk';
import type { QueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

import type { Datapoint } from '#core/lib/timeseries/enrichedRef.ts';
import { fetchDatapointsBatched } from '#core/lib/timeseries/fetch-datapoints-batched.ts';
import { tryParseDate } from '#core/lib/utils/date.ts';

import { describeType, math } from './math-config.ts';
import type { DeriveSpec } from './types.ts';
import { AGGREGATES, AGGREGATORS, type SupportedAggregate } from '#plugin-chat/lib/timeseries/aggregates.ts';
import { asTimestampedValue } from '#plugin-chat/lib/timeseries/datapoint-value.ts';
import { extractTsRef } from '#plugin-chat/lib/timeseries/extract-ts-ref.ts';

dayjs.extend(isoWeek);

// Constants

/**
 * Names of the fetcher functions exposed in derive expressions. Single
 * source of truth — referenced by the AST walker and the runtime registry.
 * The matching LLM-facing listing is inlined in the `transformDataset` tool
 * instructions (`TransformDatasetTool.tsx`, "Derive expressions" `<dl>` block);
 * keep them in lockstep when adding or removing a fetcher here.
 */
export const TIMESERIES_FETCHER_NAMES = ['datapoints', 'hourly', 'daily', 'weekly', 'monthly'] as const;
export type TimeseriesFetcherName = (typeof TIMESERIES_FETCHER_NAMES)[number];

/**
 * Default datapoint window when a fetcher is called without explicit
 * `start` / `end` opts. CDF interprets `'now'` for `end` and computes the
 * start from this offset.
 */
export const DEFAULT_DATAPOINTS_WINDOW_DAYS = 90;

/**
 * CDF granularity strings per period fetcher. `monthly` uses `'30d'` as
 * the closest available granularity — CDF's API doesn't expose calendar-
 * month buckets natively, so server-side monthly aggregates are computed
 * over rolling 30-day windows.
 */
export const GRANULARITY_BY_FETCHER: Readonly<Record<TimeseriesFetcherName, string | undefined>> = {
  datapoints: undefined,
  hourly: '1h',
  daily: '1d',
  weekly: '7d',
  monthly: '30d',
};

const DEFAULT_AGGREGATE = 'average' as const;

// Types

/**
 * Static-time view of a fetch call's options. Subset of CDF's request
 * surface restricted to what we expose to derive authors.
 */
export interface TimeseriesFetchOpts {
  start?: number | string;
  end?: number | string;
  limit?: number;
  aggregate?: string;
}

interface ParsedFetchCall {
  /** Owning derive name — used for warning attribution. */
  derive: string;
  fnName: TimeseriesFetcherName;
  /**
   * Column reference resolved from the first arg of the call. `null` when
   * the first arg is a nested function call (e.g. `monthly(datapoints(...))`)
   * — those don't drive prefetch but their inner call does.
   */
  columnRef: string | null;
  opts: TimeseriesFetchOpts;
  /**
   * `true` when the static walk could not resolve all opts to literals
   * (e.g. `monthly(ref, {start: row.someColumn})`). Defaults are used for
   * prefetch and a warning is surfaced.
   */
  dynamic: boolean;
}

export interface PrefetchCtx {
  client: CogniteClient;
  queryClient: QueryClient;
}

/**
 * Final shape held in the runtime closure: spec hash → externalId → series.
 */
type PrefetchLookup = Map<string, Map<string, ReadonlyArray<Datapoint | DatapointAggregate>>>;

export interface PrefetchResult {
  lookup: PrefetchLookup;
  warnings: string[];
}

/**
 * Map of math.js-callable fetcher functions, keyed by name. Returned by
 * {@link prefetchAndBuildRegistry} and spread into the per-row scope.
 */
export type TimeseriesFunctionRegistry = Record<TimeseriesFetcherName, (...args: unknown[]) => unknown>;

// AST helpers — tree-sitter-style structural type guards over math.js nodes.
// math.js exposes node shapes via discriminated `type` strings. We don't
// import the constructor types because the runtime AST nodes also satisfy
// these structural shapes, and we never need to construct nodes ourselves.

interface MathNodeLike {
  type: string;
  // math.js's `traverse` is documented as `cb(node, path, parent)` —
  // pre-order, depth-first. We use `parent` to detect the nested-form
  // pairing in `extractFetchCallsFromExpressions` (a `datapoints(ref)`
  // call directly inside a period fetcher's first slot inherits the
  // outer's window opts).
  traverse?: (cb: (node: MathNodeLike, path: string, parent: MathNodeLike | null) => void) => void;
}

interface FunctionNodeLike extends MathNodeLike {
  type: 'FunctionNode';
  fn: { name?: string };
  args: MathNodeLike[];
}

interface SymbolNodeLike extends MathNodeLike {
  type: 'SymbolNode';
  name: string;
}

interface AccessorNodeLike extends MathNodeLike {
  type: 'AccessorNode';
  name: string;
}

interface ConstantNodeLike extends MathNodeLike {
  type: 'ConstantNode';
  value: unknown;
}

interface ObjectNodeLike extends MathNodeLike {
  type: 'ObjectNode';
  properties: Record<string, MathNodeLike>;
}

function isFunctionNode(n: MathNodeLike): n is FunctionNodeLike {
  return n.type === 'FunctionNode';
}
function isSymbolNode(n: MathNodeLike): n is SymbolNodeLike {
  return n.type === 'SymbolNode';
}
function isAccessorNode(n: MathNodeLike): n is AccessorNodeLike {
  return n.type === 'AccessorNode';
}
function isConstantNode(n: MathNodeLike): n is ConstantNodeLike {
  return n.type === 'ConstantNode';
}
function isObjectNode(n: MathNodeLike): n is ObjectNodeLike {
  return n.type === 'ObjectNode';
}

/**
 * Resolve a fetcher call's first argument to a column name when it's a
 * direct reference (bare symbol or `row["..."]` bracket access). Returns
 * `null` for nested function calls, literals, etc. — those are handled at
 * runtime by the fetcher's array-input branch.
 */
function extractColumnRef(arg: MathNodeLike | undefined): string | null {
  if (arg === undefined) {
    return null;
  }
  if (isSymbolNode(arg)) {
    return arg.name;
  }
  if (isAccessorNode(arg)) {
    // math.js's `AccessorNode.name` produces the dotted path including the
    // `row.` prefix when bracket-accessing the row wrapper. Strip it so the
    // remaining string matches the source column name.
    const fullName = arg.name;
    return fullName.startsWith('row.') ? fullName.slice(4) : fullName;
  }
  return null;
}

/**
 * Apply a single literal property to {@link TimeseriesFetchOpts}, validating its
 * type. Returns `false` if the value couldn't be assigned (caller marks the
 * containing call as `dynamic` and falls back to defaults). Unknown keys
 * succeed silently — the API is forward-compatible with future opts.
 */
function applyFetchOptProperty(opts: TimeseriesFetchOpts, key: string, value: unknown): boolean {
  if (key === 'start' || key === 'end') {
    if (typeof value === 'string' || typeof value === 'number') {
      opts[key] = value;
      return true;
    }
    return false;
  }
  if (key === 'limit') {
    if (typeof value !== 'number') {
      return false;
    }
    opts.limit = value;
    return true;
  }
  if (key === 'aggregate') {
    if (typeof value !== 'string') {
      return false;
    }
    opts.aggregate = value;
    return true;
  }
  return true;
}

/**
 * Convert a timestamp option to the shape CDF's datapoints API accepts.
 *
 * - **Numbers** pass through unchanged (Unix milliseconds).
 * - **ISO 8601 strings** (`"2026-01-01T00:00:00Z"`, `"2026-01-01"`, …) are
 *   parsed via {@link tryParseDate} and converted to milliseconds.
 * - **CDF relative-time strings** (`"now"`, `"30d-ago"`, `"1h-ago"`) fail
 *   {@link tryParseDate} and pass through verbatim — CDF resolves them
 *   server-side. Keeping the literal string in the spec hash means the
 *   prefetch cache stays warm across calls within an evaluation pass; if
 *   we resolved them client-side, every call would shift by milliseconds
 *   and never hit cache.
 * - **Unparseable garbage** also passes through; CDF rejects it with a
 *   clean 400 ("Unrecognized time format: …") that the warning surface
 *   forwards to the LLM.
 */
function normalizeTimestamp(value: number | string | undefined): number | string | undefined {
  if (value === undefined || typeof value === 'number') {
    return value;
  }
  return tryParseDate(value)?.valueOf() ?? value;
}

/**
 * Apply {@link normalizeTimestamp} to the timestamp-shaped fields of a
 * {@link TimeseriesFetchOpts}. Called from both the static AST path
 * ({@link extractFetchOpts}) and the runtime path
 * ({@link parseRuntimeOpts}) so {@link specHash} sees the canonical form
 * on both sides — prefetch and runtime lookup hash to the same key
 * regardless of whether the user wrote ISO or millisecond literals.
 */
function normalizeFetchOpts(opts: TimeseriesFetchOpts): TimeseriesFetchOpts {
  const out: TimeseriesFetchOpts = {};
  const start = normalizeTimestamp(opts.start);
  const end = normalizeTimestamp(opts.end);
  if (start !== undefined) {
    out.start = start;
  }
  if (end !== undefined) {
    out.end = end;
  }
  if (opts.limit !== undefined) {
    out.limit = opts.limit;
  }
  if (opts.aggregate !== undefined) {
    out.aggregate = opts.aggregate;
  }
  return out;
}

/**
 * Resolve a fetcher call's optional second argument to a {@link TimeseriesFetchOpts}
 * when every property is a literal constant. Sets `dynamic = true` if any
 * value is non-literal (variable, expression, etc.) so the caller can warn
 * and fall back to defaults.
 *
 * Timestamp opts (`start`, `end`) are normalised through
 * {@link normalizeFetchOpts} before return so ISO 8601 literals and
 * millisecond literals hash to the same {@link specHash} as their runtime
 * counterparts.
 */
function extractFetchOpts(arg: MathNodeLike | undefined): { opts: TimeseriesFetchOpts; dynamic: boolean } {
  if (arg === undefined) {
    return { opts: {}, dynamic: false };
  }
  if (!isObjectNode(arg)) {
    return { opts: {}, dynamic: true };
  }

  const opts: TimeseriesFetchOpts = {};
  let dynamic = false;
  for (const [key, node] of Object.entries(arg.properties)) {
    if (!isConstantNode(node)) {
      dynamic = true;
      continue;
    }
    if (!applyFetchOptProperty(opts, key, node.value)) {
      dynamic = true;
    }
  }
  return { opts: normalizeFetchOpts(opts), dynamic };
}

// Public AST walk

/**
 * Walk each derive's AST to find timeseries-fetcher calls. Each match
 * yields a {@link ParsedFetchCall} — the basis for spec deduplication and
 * externalId collection.
 *
 * Calls with a nested function as their first argument (e.g.
 * `monthly(datapoints(ref))`) yield a record with `columnRef = null`.
 * These don't drive prefetch directly — their inner fetcher call appears
 * separately in the same walk and contributes its externalIds. The
 * outer call is handled at runtime by the registry's array-input branch.
 *
 * **Nested-form opt forwarding.** When a `datapoints(ref)` call is the
 * first argument of a period fetcher (e.g.
 * `weekly(datapoints(ref), {start, end, aggregate})`), the user's window
 * and limit naturally belong on the *outer* call but the *inner* call is
 * what drives the CDF prefetch. We forward `start`/`end`/`limit` from
 * outer onto inner here so the prefetch issues a CDF call honouring the
 * user-specified window. The outer's `aggregate` is intentionally not
 * forwarded — `datapoints` never aggregates server-side; the aggregate
 * is consumed at runtime by `clientSideBucket`. Outer wins on conflict.
 */
export function extractFetchCallsFromExpressions(derives: readonly DeriveSpec[]): {
  calls: ParsedFetchCall[];
  warnings: string[];
} {
  const calls: ParsedFetchCall[] = [];
  const warnings: string[] = [];

  for (const d of derives) {
    let root: MathNodeLike;
    try {
      root = math.parse(d.expression) as unknown as MathNodeLike;
    } catch {
      // Compilation failure is reported by the main evaluator; skip
      // silently here so we don't double-warn on the same expression.
      continue;
    }

    const visit = (node: MathNodeLike, _path: string, parent: MathNodeLike | null) => {
      if (!isFunctionNode(node)) {
        return;
      }
      const fnName = node.fn.name;
      if (typeof fnName !== 'string' || !TIMESERIES_FETCHER_NAMES.includes(fnName as TimeseriesFetcherName)) {
        return;
      }

      const [arg0, arg1] = node.args;
      const columnRef = extractColumnRef(arg0);
      const { opts: ownOpts, dynamic: ownDynamic } = extractFetchOpts(arg1);

      // Nested-form opt forwarding: when this is `datapoints(...)` sitting
      // as the first arg of a period fetcher, inherit the outer's window
      // (`start`/`end`/`limit`). Outer wins on conflict; outer's
      // `aggregate` is dropped — raw `datapoints` doesn't aggregate.
      let opts = ownOpts;
      let dynamic = ownDynamic;
      if (
        fnName === 'datapoints' &&
        parent !== null &&
        isFunctionNode(parent) &&
        typeof parent.fn.name === 'string' &&
        parent.fn.name !== 'datapoints' &&
        TIMESERIES_FETCHER_NAMES.includes(parent.fn.name as TimeseriesFetcherName) &&
        parent.args[0] === node
      ) {
        const { opts: outerOpts, dynamic: outerDynamic } = extractFetchOpts(parent.args[1]);
        opts = normalizeFetchOpts({
          start: outerOpts.start ?? ownOpts.start,
          end: outerOpts.end ?? ownOpts.end,
          limit: outerOpts.limit ?? ownOpts.limit,
          aggregate: ownOpts.aggregate,
        });
        dynamic = ownDynamic || outerDynamic;
      }

      if (dynamic) {
        warnings.push(
          `Derive "${d.name}": ${fnName}(...) opts contain non-literal values — defaults will be used for prefetch.`,
        );
      }

      calls.push({
        derive: d.name,
        fnName: fnName as TimeseriesFetcherName,
        columnRef,
        opts,
        dynamic,
      });
    };

    // math.js's `traverse` covers the root node and all descendants,
    // pre-order with `(node, path, parent)`. The root visit gets
    // `parent = null` — fine here, since a top-level fetcher call has no
    // outer fetcher to inherit opts from.
    root.traverse?.(visit);
  }

  return { calls, warnings };
}

// Spec hashing + externalId collection

/**
 * Stable, JSON-stringified key combining the fetcher name and its opts.
 * Used both at prefetch time (one batched fetch per unique key) and at
 * runtime (each fetcher closure looks up its own key in the prefetch map).
 *
 * Opts keys are emitted in a fixed order so two calls with the same
 * effective opts hash to the same string regardless of property
 * declaration order in the source expression.
 */
export function specHash(fnName: TimeseriesFetcherName, opts: TimeseriesFetchOpts): string {
  const ordered: TimeseriesFetchOpts = {};
  if (opts.start !== undefined) {
    ordered.start = opts.start;
  }
  if (opts.end !== undefined) {
    ordered.end = opts.end;
  }
  if (opts.limit !== undefined) {
    ordered.limit = opts.limit;
  }
  if (opts.aggregate !== undefined) {
    ordered.aggregate = opts.aggregate;
  }
  return JSON.stringify({ fn: fnName, opts: ordered });
}

/**
 * Project a row column value to its externalId via the shared
 * {@link extractTsRef} extractor — accepts bare strings, instance refs,
 * and {@link EnrichedTimeseriesRef}-shaped values, returns just the
 * externalId portion. Kept as a thin wrapper so call sites read as
 * "extract the externalId" rather than "extract the ref then read its
 * externalId".
 */
function extractExternalId(value: unknown): string | null {
  return extractTsRef(value)?.externalId ?? null;
}

/**
 * Group resolved fetch calls by spec hash, gathering every unique
 * externalId referenced across all rows for that spec.
 *
 * Calls with `columnRef === null` (nested form) don't contribute — their
 * inner fetcher call carries the externalIds.
 */
function collectByCallSpec(
  rows: readonly Record<string, unknown>[],
  calls: readonly ParsedFetchCall[],
): Map<string, { fnName: TimeseriesFetcherName; opts: TimeseriesFetchOpts; externalIds: Set<string> }> {
  const out = new Map<string, { fnName: TimeseriesFetcherName; opts: TimeseriesFetchOpts; externalIds: Set<string> }>();

  for (const call of calls) {
    if (call.columnRef === null) {
      continue;
    }
    const hash = specHash(call.fnName, call.opts);
    let entry = out.get(hash);
    if (!entry) {
      entry = { fnName: call.fnName, opts: call.opts, externalIds: new Set() };
      out.set(hash, entry);
    }
    for (const row of rows) {
      const externalId = extractExternalId(row[call.columnRef]);
      if (externalId !== null) {
        entry.externalIds.add(externalId);
      }
    }
  }
  return out;
}

// Spec → CDF query parameters

/**
 * Convert a parsed `(fnName, opts)` pair into the
 * {@link DatapointsFetchSpec} consumed by {@link fetchDatapointsBatched}.
 *
 * Default window (last 90 days) is materialised here so the underlying
 * fetcher call sees concrete start/end timestamps and the cache key is
 * stable across calls within the same evaluation.
 */
function toDatapointsFetchSpec(fnName: TimeseriesFetcherName, opts: TimeseriesFetchOpts) {
  const granularity = GRANULARITY_BY_FETCHER[fnName];
  const aggregate = opts.aggregate ?? (granularity === undefined ? undefined : DEFAULT_AGGREGATE);

  // Concrete defaults for the rolling window so the cache key is stable.
  const end = opts.end ?? Date.now();
  const defaultStart =
    typeof end === 'number'
      ? end - DEFAULT_DATAPOINTS_WINDOW_DAYS * 24 * 60 * 60 * 1000
      : Date.now() - DEFAULT_DATAPOINTS_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const start = opts.start ?? defaultStart;

  return {
    start,
    end,
    granularity,
    aggregates: aggregate === undefined ? undefined : [aggregate as never],
    limit: opts.limit,
  };
}

/**
 * Fetch one (fnName, opts) spec and translate the result into the
 * runtime-lookup shape. On failure, push a warning and return an empty
 * map for that spec so derive expressions degrade to `[]` rather than
 * propagating the error.
 *
 * Extracted from {@link prefetchAndBuildRegistry} so the per-spec
 * try/catch + iteration stay shallow inside the parallel `Promise.all`.
 */
async function fetchOneSpec(
  ctx: PrefetchCtx,
  fnName: TimeseriesFetcherName,
  opts: TimeseriesFetchOpts,
  externalIds: Set<string>,
  warnings: string[],
): Promise<Map<string, ReadonlyArray<Datapoint | DatapointAggregate>>> {
  if (externalIds.size === 0) {
    return new Map();
  }
  try {
    const items = Array.from(externalIds).map((externalId) => ({ externalId }));
    const fetched = await fetchDatapointsBatched({
      client: ctx.client,
      queryClient: ctx.queryClient,
      items,
      spec: toDatapointsFetchSpec(fnName, opts),
    });
    const byExt = new Map<string, ReadonlyArray<Datapoint | DatapointAggregate>>();
    for (const [externalId, series] of fetched) {
      byExt.set(externalId, series.datapoints as ReadonlyArray<Datapoint | DatapointAggregate>);
    }
    return byExt;
  } catch (e) {
    warnings.push(
      `Timeseries prefetch failed for ${fnName}: ${e instanceof Error ? e.message : String(e)}. ` +
        'Affected derive expressions return empty datapoint arrays.',
    );
    return new Map();
  }
}

/**
 * Run the full prefetch pass for a set of derive expressions and build the
 * runtime fetcher registry. Returned registry is injected into the per-row
 * math.js scope by {@link evaluateDerivedFields}.
 *
 * No fetches are issued when:
 *  - derives reference no fetcher functions, or
 *  - none of the rows resolve any externalIds for the discovered specs.
 *
 * Failures per spec degrade gracefully: a warning is collected, the
 * spec's lookup map is left empty, and runtime fetcher calls return `[]`.
 */
export async function prefetchAndBuildRegistry({
  rows,
  derives,
  ctx,
}: {
  rows: readonly Record<string, unknown>[];
  derives: readonly DeriveSpec[];
  ctx: PrefetchCtx | undefined;
}): Promise<{ registry: TimeseriesFunctionRegistry; warnings: string[] }> {
  const { calls, warnings: parseWarnings } = extractFetchCallsFromExpressions(derives);
  const lookup: PrefetchLookup = new Map();
  const warnings: string[] = [...parseWarnings];

  if (calls.length === 0 || rows.length === 0 || ctx === undefined) {
    return { registry: buildTimeseriesFunctionRegistry(lookup), warnings };
  }

  const grouped = collectByCallSpec(rows, calls);

  // Issue one batched fetch per unique spec, in parallel.
  const entries = await Promise.all(
    Array.from(grouped.entries()).map(async ([hash, { fnName, opts, externalIds }]) => {
      const byExt = await fetchOneSpec(ctx, fnName, opts, externalIds, warnings);
      return [hash, byExt] as const;
    }),
  );
  for (const [hash, byExt] of entries) {
    lookup.set(hash, byExt);
  }

  return { registry: buildTimeseriesFunctionRegistry(lookup), warnings };
}

// Runtime registry — math.js-callable fetchers

/**
 * Build the runtime fetcher registry from a prefetched lookup.
 *
 * Each fetcher dispatches on its first argument:
 * - **null / undefined**: graceful — returns `[]`. Sparse-data flows
 *   (a row without the ref column) propagate as empty datapoints rather
 *   than blowing up the whole derive.
 * - **Array-shaped** (already-prefetched `Datapoint[]`): client-side
 *   bucket per fetcher's period. The `datapoints` fetcher is the
 *   identity in this branch. Used for nested forms like
 *   `monthly(datapoints(ref))`.
 * - **Ref-shaped** (string / `{externalId}` / `EnrichedTimeseriesRef`):
 *   compute the spec hash from the runtime fetcher name + opts and look
 *   up the prefetched series. Returns `[]` when no series for the
 *   externalId is in the lookup (CDF returned nothing — legitimate
 *   "no data in window" signal).
 * - **Anything else** (number, boolean, plain object without externalId,
 *   etc.): throws `TypeError`. Surfaces LLM mistakes (e.g. calling
 *   `weekly(rate)` where `rate` is already a number) via
 *   `evaluateCompiledField`'s error envelope rather than silently
 *   nulling out and letting the LLM infer "no data exists".
 */
export function buildTimeseriesFunctionRegistry(lookup: PrefetchLookup): TimeseriesFunctionRegistry {
  const registry = {} as TimeseriesFunctionRegistry;
  for (const fnName of TIMESERIES_FETCHER_NAMES) {
    registry[fnName] = (refOrArray: unknown, runtimeOpts?: unknown) => {
      // Nullish input: missing data, propagate as empty datapoints. Matches
      // the rest of the pipeline's "missing input → missing output" rule.
      if (refOrArray === null || refOrArray === undefined) {
        return [];
      }

      // Nested-form path: first arg is already an array of datapoints.
      // Client-side bucket per the fetcher's period (or identity for
      // `datapoints`). The runtime opts (`{start, end, aggregate}`) are
      // forwarded so the bucket honours the user's window and aggregator
      // — see `clientSideBucket` for the contract.
      if (Array.isArray(refOrArray)) {
        if (fnName === 'datapoints') {
          return refOrArray;
        }
        return clientSideBucket(fnName, refOrArray, parseRuntimeOpts(runtimeOpts));
      }

      // Server-side path: resolve the externalId, hash the opts, look up.
      // A non-null non-array value that doesn't carry a ts-ref shape is an
      // LLM mistake — throw so the error envelope tells them what was
      // passed rather than silently producing an empty result.
      const externalId = extractExternalId(refOrArray);
      if (externalId === null) {
        throw new TypeError(`${fnName} expected a timeseries reference, got ${describeType(refOrArray)}`);
      }

      const opts = parseRuntimeOpts(runtimeOpts);
      const hash = specHash(fnName, opts);
      const byExt = lookup.get(hash);
      const series = byExt?.get(externalId);
      return series ? [...series] : [];
    };
  }
  return registry;
}

/**
 * Coerce the runtime second-argument value to a {@link TimeseriesFetchOpts},
 * applying the same {@link normalizeFetchOpts} pass as {@link extractFetchOpts}
 * so a static-time {@link specHash} matches the runtime hash for identical
 * literal options.
 */
function parseRuntimeOpts(value: unknown): TimeseriesFetchOpts {
  if (value === null || value === undefined || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const obj = value as Record<string, unknown>;
  const opts: TimeseriesFetchOpts = {};
  if (typeof obj.start === 'string' || typeof obj.start === 'number') {
    opts.start = obj.start;
  }
  if (typeof obj.end === 'string' || typeof obj.end === 'number') {
    opts.end = obj.end;
  }
  if (typeof obj.limit === 'number') {
    opts.limit = obj.limit;
  }
  if (typeof obj.aggregate === 'string') {
    opts.aggregate = obj.aggregate;
  }
  return normalizeFetchOpts(opts);
}

/**
 * Map a period fetcher to a dayjs format key + truncation unit. The format
 * key produces a stable bucket label; the truncation unit recovers the
 * bucket-start `Date` for the synthesized datapoint's timestamp.
 *
 * `monthly` uses calendar-month buckets here (rather than the rolling-30d
 * server-side equivalent). The discrepancy is intentional — server-side
 * aggregates request CDF's `'30d'` granularity for efficiency; client-side
 * bucketing groups by calendar month, which is usually closer to user
 * expectations for ad-hoc nested forms like `monthly(datapoints(...))`.
 *
 * `'isoWeek'` is provided by the dayjs/plugin/isoWeek extension loaded at
 * the top of this module; the literal type sits outside `OpUnitType` so we
 * accept the augmented form via `as const` inference rather than a wider
 * annotation that would silently allow other unsupported units.
 */
const BUCKET_BY_FETCHER = {
  hourly: { key: 'YYYY-MM-DD HH', unit: 'hour' },
  daily: { key: 'YYYY-MM-DD', unit: 'day' },
  weekly: { key: 'GGGG-[W]WW', unit: 'isoWeek' },
  monthly: { key: 'YYYY-MM', unit: 'month' },
} as const;

/**
 * Bucket an array of datapoints into the period implied by `fnName`,
 * applying the requested aggregator from {@link AGGREGATORS}. Output
 * mirrors CDF's `DatapointAggregate[]` shape: `{timestamp, [aggregate]: v}`
 * per bucket (e.g. `{timestamp, min: 12.3}` for `aggregate: 'min'`).
 *
 * Used for the nested form `weekly(datapoints(ref), {start, end, aggregate})`
 * where the bucketing happens client-side on already-fetched datapoints.
 *
 * **Window filtering.** `opts.start` / `opts.end` filter datapoints
 * before bucketing. Only numeric bounds (Unix ms) are honoured —
 * relative-time strings like `'30d-ago'` aren't resolved client-side and
 * fall through (no filter on that bound). Static-time ISO 8601 literals
 * have already been normalised to ms by `normalizeFetchOpts` upstream.
 *
 * **Aggregate dispatch.** `opts.aggregate` defaults to `DEFAULT_AGGREGATE`
 * (`'average'`) when absent. Unknown aggregate names throw `TypeError` so
 * the LLM-facing error envelope reports the mistake rather than silently
 * averaging.
 *
 * Datapoints lacking a usable timestamp or numeric value are skipped.
 * Empty buckets aren't synthesized — only buckets with at least one
 * contributing datapoint appear in the output.
 */
function clientSideBucket(
  fnName: Exclude<TimeseriesFetcherName, 'datapoints'>,
  arr: readonly unknown[],
  opts: TimeseriesFetchOpts,
): DatapointAggregate[] {
  const aggregate = opts.aggregate ?? DEFAULT_AGGREGATE;
  if (!AGGREGATES.includes(aggregate as SupportedAggregate)) {
    throw new TypeError(`${fnName}: unsupported aggregate "${aggregate}". Expected one of: ${AGGREGATES.join(', ')}.`);
  }
  const reduce = AGGREGATORS[aggregate as SupportedAggregate];

  const startMs = typeof opts.start === 'number' ? opts.start : undefined;
  const endMs = typeof opts.end === 'number' ? opts.end : undefined;

  const config = BUCKET_BY_FETCHER[fnName];
  const buckets = new Map<string, { values: number[]; bucketStart: Date }>();

  for (const dp of arr) {
    const tv = asTimestampedValue(dp);
    if (tv === null) {
      continue;
    }
    const tsMs = tv.ts.getTime();
    if (startMs !== undefined && tsMs < startMs) {
      continue;
    }
    if (endMs !== undefined && tsMs > endMs) {
      continue;
    }
    const m = dayjs(tv.ts);
    const key = m.format(config.key);
    const entry = buckets.get(key) ?? {
      values: [],
      // `as dayjs.OpUnitType` because dayjs's TS surface for `startOf`
      // doesn't include `'isoWeek'`, even though the plugin loaded above
      // accepts it at runtime.
      bucketStart: m.startOf(config.unit as dayjs.OpUnitType).toDate(),
    };
    entry.values.push(tv.value);
    if (!buckets.has(key)) {
      buckets.set(key, entry);
    }
  }

  return Array.from(buckets.values())
    .map(({ values, bucketStart }) => ({
      timestamp: bucketStart,
      // Emit only the field matching the requested aggregate so
      // downstream `extractDatapointValue` resolves unambiguously
      // against `DATAPOINT_VALUE_FIELDS`.
      [aggregate]: reduce(values),
    }))
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()) as DatapointAggregate[];
}
